import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FormProvider } from 'react-hook-form'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import Input from 'components/generic/form/Input'
import {
  CONSUMER_INFO_FIELDS,
  SUPPLIER_INFO_FIELDS,
  salesOrderDetailFields,
  purchaseOrderDetailFields,
  paymentInfoFields,
} from 'utils/constants'
import SubFormLayout from 'components/layouts/SubFormLayout'
import FileUploader from 'components/generic/file-uploader/FileUploader'
import ContractMaterialsList from 'components/containers/contracts/ContractMaterialsList'
import FreightDestination from 'components/containers/contracts/FreightDestination'
import { useContractHeader } from 'components/containers/contracts/useContractHeader'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import { checkIfDateIsLocked } from 'utils/helpers'

const ContractContainerStatic = ({
  isSales,
  methods,
  isLoadingProps,
  customerData,
  contractData,
  initialMaterials,
}) => {
  const { control, watch } = methods
  const theme = useTheme()
  const materialsApiRef = useGridApiRef()
  const isLoading = isLoadingProps || false
  const contract = isSales ? 'sales' : 'purchase'
  const { canUploadFilesSO, canUploadFilesPO, enableLockAccountingV2, enableSoExport } = useSelector(
    state => state.featureFlags?.variables || {},
  )
  const canUploadFiles = isSales ? canUploadFilesSO : canUploadFilesPO

  const { settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const defaultCurrency = settings?.defaultCurrency

  const orderCurrency = watch('currency')
  const { pricePrepend } = useGetPricePrepend({ selectedCurrency: orderCurrency })

  const orderData = useMemo(() => {
    const contractLocation =
      contractData?.location ||
      customerData?.locations?.find(({ id }) => id === contractData?.[`${contract}OrderLocationId`])

    return {
      ...contractData,
      contractLocation,
    }
  }, [contractData, customerData])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!contractData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false

    const effectiveDateIsLocked = checkIfDateIsLocked(
      lockAccountingPeriod,
      contractData?.[isSales ? 'orderSalesDetails' : 'order']?.effectiveDate,
    )
    const expirationDateIsLocked = checkIfDateIsLocked(
      lockAccountingPeriod,
      contractData?.[isSales ? 'orderSalesDetails' : 'order']?.expirationDate,
    )
    return effectiveDateIsLocked || expirationDateIsLocked
  }, [lockAccountingPeriod, contractData, enableLockAccountingV2, isSales])

  const { COPrintDialog } = useContractHeader({ isSales, contractData, isInLockedAccountingPeriodV2 })
  const showCustomFxRate = orderCurrency !== defaultCurrency

  const filteredSalesOrderDetailFields = useMemo(() => {
    return enableSoExport ? salesOrderDetailFields : salesOrderDetailFields.slice(0, -1)
  }, [enableSoExport, salesOrderDetailFields])

  return (
    <>
      <COPrintDialog orderData={orderData} materials={initialMaterials} pricePrepend={pricePrepend} />
      <FormProvider {...methods}>
        <form>
          <Stack direction={{ xs: 'column', md: 'row' }} p={3} spacing={3}>
            <SubFormLayout
              formName={`${isSales ? 'Consumer' : 'Supplier'} Information`}
              fields={isSales ? CONSUMER_INFO_FIELDS : SUPPLIER_INFO_FIELDS}
            />
            <SubFormLayout
              formName="Order Details"
              fields={isSales ? filteredSalesOrderDetailFields : purchaseOrderDetailFields}
            />
            <SubFormLayout
              formName="Payment Info"
              fields={[
                ...paymentInfoFields.slice(0, 2),
                ...(showCustomFxRate && orderData?.customForeignExchangeRateEnabled ? [paymentInfoFields[2]] : []),
              ]}
            />
          </Stack>
          <ContractMaterialsList
            isView
            control={control}
            materials={initialMaterials}
            apiRef={materialsApiRef}
            loading={isLoading}
            priceType={isSales ? 'sellPrice' : 'buyPrice'}
            pricePrepend={pricePrepend}
            isTransfer={customerData && !!customerData?.isYard}
          />
          {isSales ? <FreightDestination isView methods={methods} loading={isLoading} /> : null}
          <Box p={3}>
            <Stack direction="row" mb={1}>
              <Typography variant="h6" gutterBottom color="textSecondary">
                Notes
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Input
                name="notes"
                disabled
                defaultValue=""
                control={control}
                fullWidth
                multiline
                sx={{
                  'textarea.Mui-disabled': {
                    WebkitTextFillColor: theme.palette.text.primary,
                  },
                }}
              />
              {canUploadFiles && (
                <div style={{ marginLeft: '1.5em' }}>
                  <FileUploader filesData={contractData?.docs || []} viewOnly />
                </div>
              )}
            </Stack>
          </Box>
        </form>
      </FormProvider>
    </>
  )
}

ContractContainerStatic.propTypes = {
  isSales: PropTypes.bool,
  methods: PropTypes.object,
  isLoadingProps: PropTypes.bool,
  customerData: PropTypes.object,
  contractData: PropTypes.object,
  initialMaterials: PropTypes.array,
}

ContractContainerStatic.defaultProps = {
  isSales: false,
  methods: {},
  isLoadingProps: false,
  customerData: {},
  contractData: {},
  initialMaterials: [],
}

export default ContractContainerStatic
